import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, ShieldAlert, ShieldCheck, Lock, CheckCircle2, AlertTriangle } from 'lucide-react';

/**
 * Security Status Banner
 * 
 * Shows user's current security level and encourages enabling optional 2FA.
 * Part of the security enhancement framework that makes 2FA optional to avoid user abandonment.
 */
const SecurityStatusBanner = ({ user, onNavigateToSettings }) => {
  const navigate = useNavigate();
  
  // Determine security level
  const has2FA = user?.has_2fa || false;
  const hasUsername = user?.username || false;
  
  // Calculate security score
  let securityLevel = 'basic';
  let securityScore = 1; // Everyone has password
  
  if (hasUsername) securityScore += 1;
  if (has2FA) securityScore += 2;
  
  if (securityScore >= 3) {
    securityLevel = 'enhanced';
  } else if (securityScore >= 2) {
    securityLevel = 'good';
  }
  
  const handleEnableSecurity = () => {
    if (onNavigateToSettings) {
      onNavigateToSettings('security');
    } else {
      // Fallback: navigate to dashboard with security tab
      navigate('/dashboard?tab=settings&section=security');
    }
  };
  
  // If user already has enhanced security, show success banner
  if (has2FA) {
    return (
      <Alert className="bg-green-50 border-green-200">
        <ShieldCheck className="h-5 w-5 text-green-600" />
        <AlertDescription className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <span className="font-semibold text-green-900">Enhanced Security Active</span>
            <p className="text-sm text-green-700 mt-1">
              Your account is protected with Two-Factor Authentication and meets security best practices.
            </p>
          </div>
          <Badge className="bg-green-600 text-white whitespace-nowrap">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Secured
          </Badge>
        </AlertDescription>
      </Alert>
    );
  }
  
  // If user doesn't have 2FA, encourage adoption
  return (
    <Alert className="bg-blue-50 border-blue-200">
      <Shield className="h-5 w-5 text-blue-600" />
      <AlertDescription className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-blue-900">Boost Your Security (Optional)</span>
            <Badge variant="outline" className="border-blue-300 text-blue-700">
              Recommended
            </Badge>
          </div>
          <p className="text-sm text-blue-700 mt-1">
            Enable Two-Factor Authentication to add an extra layer of protection to your account. 
            {!hasUsername && " You can also set a username for easier login."}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-blue-600">
            <div className="flex items-center">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              <span>Password Protection</span>
            </div>
            {hasUsername && (
              <div className="flex items-center">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                <span>Username Set</span>
              </div>
            )}
            <div className="flex items-center text-blue-400">
              <AlertTriangle className="w-3 h-3 mr-1" />
              <span>2FA Not Enabled</span>
            </div>
          </div>
        </div>
        <Button
          onClick={handleEnableSecurity}
          size="sm"
          className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap w-full sm:w-auto"
          data-testid="overview-enable-2fa-btn"
        >
          <Lock className="w-3 h-3 mr-1" />
          Set up 2FA in Security
        </Button>
      </AlertDescription>
    </Alert>
  );
};

export default SecurityStatusBanner;
